import { useEffect, useState } from "react";
import type { AdminGoldGrant, AdminUserSummary, MediaItem, VaultStatus } from "@gooner/shared";
import { clipKey } from "@gooner/shared";
import { fmtBytes } from "../control/lib/format";
import { adminApi } from "../lib/api";
import { Button, TextInput, cx } from "../control/ui/primitives";

/**
 * Accounts + GoonLords Gold grants.
 *
 * This is the v0 fulfillment loop: someone pays at the hosted checkout with their
 * goonlords username in the note, the operator finds them here and grants the days.
 * It stays after the webhook automates that — comps, refunds-as-revokes, support.
 *
 */
export function UsersAdmin() {
  const [users, setUsers] = useState<AdminUserSummary[] | null>(null);
  const [q, setQ] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [grants, setGrants] = useState<Record<string, AdminGoldGrant[]>>({});
  // Loaded beside the grants on expand: sight and a delete, per account (no queue).
  const [vaults, setVaults] = useState<Record<string, { vault: VaultStatus; items: MediaItem[] }>>({});
  const [customDays, setCustomDays] = useState<Record<string, string>>({});
  /** Temporary passwords just issued, shown once on the row they belong to. */
  const [temps, setTemps] = useState<Record<string, string>>({});

  async function load(query = q) {
    try {
      const r = await adminApi.listUsers(query.trim() || undefined);
      setUsers(r.users);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }
  useEffect(() => {
    void load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function grant(u: AdminUserSummary, days: number) {
    setBusy(u.id);
    try {
      await adminApi.grantGold(u.id, days);
      await load();
      if (open === u.id) setGrants((g) => ({ ...g, [u.id]: [] }));
      setOpen(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function revoke(u: AdminUserSummary) {
    // A revoke ends a paid state; one deliberate confirm matches the delete idiom.
    if (!window.confirm(`End ${u.username}'s Premium now?`)) return;
    setBusy(u.id);
    try {
      await adminApi.revokeGold(u.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function resetPassword(u: AdminUserSummary) {
    // Replaces their password and signs them out everywhere — one deliberate confirm.
    if (!window.confirm(`Reset ${u.username}'s password? They'll be signed out everywhere and need the temporary password you pass on.`)) return;
    setBusy(u.id);
    try {
      const r = await adminApi.resetPassword(u.id);
      setTemps((t) => ({ ...t, [u.id]: r.password }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function toggleLedger(u: AdminUserSummary) {
    if (open === u.id) {
      setOpen(null);
      return;
    }
    setOpen(u.id);
    if (!grants[u.id]) {
      const r = await adminApi.listGoldGrants(u.id).catch(() => null);
      if (r) setGrants((g) => ({ ...g, [u.id]: r.grants }));
    }
    if (!vaults[u.id]) {
      const v = await adminApi.userVault(u.id).catch(() => null);
      if (v) setVaults((m) => ({ ...m, [u.id]: v }));
    }
  }

  async function deleteUpload(u: AdminUserSummary, clipId: string) {
    // Deleting someone's file is the one irreversible thing on this page — one confirm.
    if (!window.confirm(`Delete this upload from ${u.username}'s vault?`)) return;
    setBusy(u.id);
    try {
      await adminApi.deleteUserVaultItem(u.id, clipId);
      const v = await adminApi.userVault(u.id).catch(() => null);
      if (v) setVaults((m) => ({ ...m, [u.id]: v }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const now = Date.now();
  const date = (ms: number) => new Date(ms).toLocaleDateString();

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-zinc-100">Users</h1>
          <p className="text-xs text-zinc-500">
            Accounts and their GoonLords Premium. Granting extends from whatever is left;
            revoking ends it now and keeps the ledger.
          </p>
        </div>
        {users ? (
          <span className="rounded-full bg-[var(--color-panel-2)] px-2.5 py-1 text-xs font-medium text-zinc-300">
            {users.filter((u) => (u.goldUntil ?? 0) > now).length} gold · {users.length} shown
          </span>
        ) : null}
      </header>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void load();
        }}
      >
        <TextInput
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search usernames…"
          className="max-w-xs"
        />
        <Button type="submit" variant="subtle">
          Search
        </Button>
      </form>

      {error ? (
        <p className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          {error}
        </p>
      ) : null}
      {!users ? <p className="text-sm text-zinc-500">Loading…</p> : null}
      {users && users.length === 0 ? <p className="text-sm text-zinc-500">No accounts match.</p> : null}

      <ul className="space-y-2">
        {(users ?? []).map((u) => {
          const gold = (u.goldUntil ?? 0) > now;
          const lapsed = u.goldUntil != null && !gold;
          return (
            <li
              key={u.id}
              className="rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel)] px-3 py-2"
            >
              <div className="flex flex-wrap items-center gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-zinc-100">{u.username}</span>
                    {gold ? (
                      <span className="rounded-full border border-[var(--color-accent)]/50 bg-[var(--color-accent)]/10 px-1.5 text-[9px] font-semibold uppercase tracking-wide text-[var(--color-accent-soft)]">
                        Premium
                      </span>
                    ) : null}
                  </div>
                  <p className="text-xs text-zinc-500">
                    joined {date(u.createdAt)}
                    {gold ? ` · gold until ${date(u.goldUntil as number)}` : ""}
                    {lapsed ? ` · gold lapsed ${date(u.goldUntil as number)}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button size="sm" disabled={busy === u.id} onClick={() => void grant(u, 7)}>
                    +7 d
                  </Button>
                  <Button size="sm" disabled={busy === u.id} onClick={() => void grant(u, 30)}>
                    +30 d
                  </Button>
                  <Button size="sm" disabled={busy === u.id} onClick={() => void grant(u, 365)}>
                    +365 d
                  </Button>
                  <TextInput
                    value={customDays[u.id] ?? ""}
                    onChange={(e) => setCustomDays((c) => ({ ...c, [u.id]: e.target.value }))}
                    placeholder="days"
                    className="w-16"
                  />
                  <Button
                    size="sm"
                    disabled={busy === u.id || !/^\d+$/.test(customDays[u.id] ?? "")}
                    onClick={() => void grant(u, Number(customDays[u.id]))}
                  >
                    Grant
                  </Button>
                  {gold ? (
                    <Button size="sm" variant="danger" disabled={busy === u.id} onClick={() => void revoke(u)}>
                      Revoke
                    </Button>
                  ) : null}
                  <Button size="sm" variant="ghost" onClick={() => void toggleLedger(u)}>
                    {open === u.id ? "Hide" : "Ledger"}
                  </Button>
                </div>
              </div>
              {open === u.id ? (
                <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-[var(--color-edge)] pt-2 text-xs text-zinc-400">
                  <span>Password:</span>
                  {temps[u.id] ? (
                    <span>
                      temporary <code className="select-all rounded bg-[var(--color-panel-2)] px-1.5 py-0.5 text-zinc-100">{temps[u.id]}</code>{" "}
                      — give it only to someone you've confirmed owns the account; they change it in Settings.
                    </span>
                  ) : (
                    <Button size="sm" variant="subtle" disabled={busy === u.id} onClick={() => void resetPassword(u)}>
                      Reset password
                    </Button>
                  )}
                </div>
              ) : null}
              {open === u.id && vaults[u.id] ? (
                <div className="mt-2 border-t border-[var(--color-edge)] pt-2 text-xs text-zinc-400">
                  <p>
                    Vault: {fmtBytes(vaults[u.id].vault.usedBytes)} of{" "}
                    {fmtBytes(vaults[u.id].vault.baseBytes + vaults[u.id].vault.extraBytes)} ·{" "}
                    {vaults[u.id].items.length} upload{vaults[u.id].items.length === 1 ? "" : "s"}
                  </p>
                  {vaults[u.id].items.length > 0 ? (
                    <ul className="mt-1 flex flex-wrap gap-2">
                      {vaults[u.id].items.map((item) => (
                        <li key={clipKey(item)} className="flex items-center gap-1 rounded border border-[var(--color-edge)] p-1">
                          {/* The file itself, first frame — an upload has no poster and no
                              preview by rule, so there is nothing else to show. */}
                          {item.kind === "video" ? (
                            <video src={item.url} muted preload="metadata" className="h-12 w-16 rounded object-cover" />
                          ) : (
                            <img src={item.url} alt="" className="h-12 w-16 rounded object-cover" />
                          )}
                          <Button size="sm" variant="danger" disabled={busy === u.id} onClick={() => void deleteUpload(u, clipKey(item))}>
                            Delete
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
              {open === u.id ? (
                <ul className="mt-2 space-y-1 border-t border-[var(--color-edge)] pt-2">
                  {(grants[u.id] ?? []).length === 0 ? (
                    <li className="text-xs text-zinc-500">No grants yet.</li>
                  ) : (
                    (grants[u.id] ?? []).map((g) => (
                      <li key={g.id} className="flex items-center gap-2 text-xs text-zinc-400">
                        <span className={cx("font-medium", g.days === 0 ? "text-red-300" : "text-zinc-200")}>
                          {g.days === 0 ? "revoked" : `+${g.days} d`}
                        </span>
                        <span>{g.source}</span>
                        {g.note ? <span className="truncate text-zinc-500">{g.note}</span> : null}
                        <span className="ml-auto shrink-0">{date(g.createdAt)}</span>
                      </li>
                    ))
                  )}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
